/**
 * BottomNav Component
 * 
 * Provides an Instagram-style bottom navigation for the application:
 * - Fixed at the bottom of the screen
 * - Mobile-optimized with large tap targets
 * - Simple icon + label navigation pattern
 * - Visual feedback for active state
 * - Built with iOS-style animations and design principles
 * 
 * @author Your Name
 * @version 1.0
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../firebase/AuthContext';
import './BottomNav.css';

/**
 * BottomNav Component
 * 
 * @returns {JSX.Element} Mobile-optimized bottom navigation bar
 */
function BottomNav() {
  // Get current location to determine active nav item
  const location = useLocation();
  
  // Get auth context for conditional rendering
  const { currentUser } = useAuth();

  /**
   * Determines if a nav item is active based on the current path
   * 
   * @param {string} path - The path to check
   * @returns {boolean} Whether the path is active
   */
  const isActive = (path) => {
    // Special case for home path
    if (path === '/' && location.pathname === '/') return true;
    
    // For other paths, check if current path starts with the given path
    // This handles nested routes
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    
    return false;
  };

  return (
    <nav className="bottom-nav">
      <div className="bottom-nav-container">
        {/* Home navigation item */}
        <Link to="/" className={`nav-item ${isActive('/') ? 'active' : ''}`}>
          <div className="nav-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <span className="nav-label">Home</span>
        </Link>

        {/* Leaderboard navigation item */}
        <Link to="/leaderboard" className={`nav-item ${isActive('/leaderboard') ? 'active' : ''}`}>
          <div className="nav-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <rect x="8" y="2" width="8" height="4"></rect>
              <line x1="12" y1="6" x2="12" y2="10"></line>
              <polyline points="17 6 22 11 17 16"></polyline>
              <polyline points="7 6 2 11 7 16"></polyline>
              <line x1="2" y1="11" x2="22" y2="11"></line>
              <line x1="12" y1="10" x2="12" y2="22"></line>
            </svg>
          </div>
          <span className="nav-label">Leaders</span>
        </Link>

        {/* Profile navigation item - only if logged in */}
        {currentUser ? (
          <Link to="/profile" className={`nav-item ${isActive('/profile') ? 'active' : ''}`}>
            <div className="nav-icon">
              {currentUser.photoURL ? (
                <img 
                  src={currentUser.photoURL} 
                  alt="Profile" 
                  className="profile-photo"
                />
              ) : (
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              )}
            </div>
            <span className="nav-label">Profile</span>
          </Link>
        ) : (
          // Login navigation item - only if not logged in
          <Link to="/login" className={`nav-item ${isActive('/login') ? 'active' : ''}`}>
            <div className="nav-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                <polyline points="10 17 15 12 10 7"></polyline>
                <line x1="15" y1="12" x2="3" y2="12"></line>
              </svg>
            </div>
            <span className="nav-label">Sign In</span>
          </Link>
        )}
      </div>
    </nav>
  );
}

export default BottomNav; 